import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

type SessionNavigationContextValue = {
  /** True while any session navigation (e.g. useLogin getToken + fetchAccount) is in flight */
  isPending: boolean
  beginSessionNavigation: () => void
  endSessionNavigation: () => void
}

const SessionNavigationContext = createContext<SessionNavigationContextValue>({
  isPending: false,
  beginSessionNavigation: () => undefined,
  endSessionNavigation: () => undefined,
})

export const SessionNavigationProvider = ({ children }: { children: React.ReactNode }) => {
  const [pendingCount, setPendingCount] = useState(0)

  const beginSessionNavigation = useCallback(() => {
    setPendingCount(c => c + 1)
  }, [])

  const endSessionNavigation = useCallback(() => {
    setPendingCount(c => Math.max(0, c - 1))
  }, [])

  const value = useMemo(
    () => ({
      isPending: pendingCount > 0,
      beginSessionNavigation,
      endSessionNavigation,
    }),
    [pendingCount, beginSessionNavigation, endSessionNavigation],
  )

  return <SessionNavigationContext.Provider value={value}>{children}</SessionNavigationContext.Provider>
}

export const useSessionNavigation = (): SessionNavigationContextValue => useContext(SessionNavigationContext)
